import { useMemo, useState } from 'react'
import {
  Input,
  Autocomplete,
  Button,
  Typography,
  FormTable
} from '@fullscreendigital/carrefour-dex-fe'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { buildColumns, getInitialValues, getValidationSchema } from './helpers'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { CircularProgress, Grid } from '@mui/material'
import { makeCoreGetRequest, makeCorePostRequest } from 'utils/api'
import { useMutation, useQuery } from 'react-query'
import { toTableRowItems } from '../../InProgressSeals/serializers'
import { DeleteConfirmationModal } from '../../../../../core-ui/DeleteConfirmationModal'
import { toShopItems } from './serializers'
import { FormInfoItems } from 'components/core-ui/FormInfoItems'

interface ReturoReportProps {
  isModalOpen: boolean
  closeModal: Function
  setShouldRefreshData: Function
  isEdit?: boolean
  editItem?: ReturoReport
}

const EditReportForm: React.FC<ReturoReportProps> = props => {
  const { isModalOpen, closeModal, isEdit, editItem, setShouldRefreshData } = props

  const { showNotification } = useNotification()

  const [shopIds, setShopIds] = useState<ShopOption[]>([])
  const [tableItems, setTableItems] = useState<ReturoSeal[]>([])
  const [rows, setRows] = useState([])
  const [shouldRefreshSealData, setShouldRefreshSealData] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)

  const openDeleteModal = (): void => setIsDeleteModalOpen(true)

  const closeDeleteModal = (): void => setIsDeleteModalOpen(false)

  const [selectedSealId, setSelectedSealId] = useState<ReturoSeal>()

  const mutatePV = async values => {
    await makeCorePostRequest(
      `/shop-reception/returo-notice/detail/${isEdit ? editItem.id : 0}`,
      values
    )
  }

  const { isLoading, mutate } = useMutation(mutatePV, {
    onSuccess: () => {
      showNotification({
        message: `Procesul verbal a fost ${editItem ? 'modificat' : 'creat'} cu succes.`,
        type: 'success'
      })

      setShouldRefreshData(true)
      closeModal()
      formik.resetForm()
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare la ${
          editItem ? 'modificarea' : 'crearea'
        } procesului verbal. ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const {
    data: userShops,
    isLoading: isShopTypesLoading,
    isFetching: isShopTypesFetching
  } = useQuery(
    ['user-shops'],
    async () => {
      const result = await makeCoreGetRequest('/shop-reception-mobile/home/user-shops', null)
      const options = toShopItems(result)

      setShopIds(options)
      return options
    },
    {
      onError: error => {
        showNotification({
          message: `A apărut o eroare la obtinerea magazinelor utilizatorului. ${formatErrorMessage(
            error
          )}`,
          type: 'error'
        })
      },
      enabled: isModalOpen
    }
  )

  const { isLoading: isSealListLoading } = useQuery(
    ['returo-notice-seals', editItem?.id],
    async () => {
      const response = await makeCoreGetRequest(
        `/shop-reception/returo-notice/detail/${editItem ? editItem.id : '0'}`
      )
      const responseRows = toTableRowItems(response.returoSealModelList)
      setTableItems(responseRows)
      setRows(responseRows)
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare la obtinerea datelor despre procesul verbal. ${formatErrorMessage(
            error
          )}`,
          type: 'error'
        })
      },
      enabled: isModalOpen
    }
  )

  const initialValues = useMemo(
    () => getInitialValues(editItem, userShops, shopIds, rows),
    [editItem, userShops, shopIds, rows]
  )
  const validationSchema = useMemo(() => getValidationSchema(editItem), [editItem])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: any, { resetForm }) => {
      try {
        const payload = {
          ...values
        }
        mutate(payload)
      } catch (error) {
        showNotification({
          message: `A aparut o eroare la modificarea datelor. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  })

  const toggleSealForNotice = (row, checked) => {
    const newSealsList = checked
      ? formik.values.returoSealModelList.map(item =>
          +item.id === +row.id ? { ...item, idNotice: editItem?.id, selectedForNotice: true } : item
        )
      : formik.values.returoSealModelList.map(item =>
          +item.id === +row.id ? { ...item, idNotice: 0, selectedForNotice: false } : item
        )

    formik.setFieldValue('returoSealModelList', newSealsList)
    setTableItems(newSealsList)
  }

  const deleteSeal = rowId => {
    setSelectedSealId(rowId)
    openDeleteModal()
  }

  const columns = useMemo(
    () =>
      buildColumns({
        toggleSealForNotice: toggleSealForNotice,
        deleteSeal: deleteSeal,
        idNotice: editItem?.id
      }),
    [toggleSealForNotice]
  )

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      modalTitle={isEdit ? 'Editează proces verbal' : 'Adaugă proces verbal'}
      modalWidth="85%"
    >
      <Grid container spacing={2}>
        {isEdit && (
          <Grid xs={12} item>
            <FormInfoItems
              items={[
                {
                  label: 'Număr PV:',
                  name: editItem?.noticeNumber,
                  itemStyle: { gridTemplateColumns: '100px 1fr' }
                }
              ]}
            />
          </Grid>
        )}
        <Grid xs={12} item>
          {isShopTypesLoading || isShopTypesFetching ? (
            <div style={{ display: 'flex' }}>
              <CircularProgress color="primary" disableShrink />
            </div>
          ) : (
            <Autocomplete
              variant="outlined"
              label="Magazine"
              disableClearable
              onChange={(e: any, option: any) => {
                formik.setFieldValue('shopCode', option.shopCode)
              }}
              error={!!formik.errors.shopIds}
              helperText={formik.errors.shopIds as string}
              options={formik.values.shopIds || []}
              disabled={editItem?.status === 'PROCESSED'}
              value={formik.values.shopIds.find(s => s.shopCode === formik.values.shopCode) || null}
              required
            />
          )}
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.cmrNumber}
            onChange={event =>
              formik.setFieldValue('cmrNumber', (event.target as HTMLInputElement).value)
            }
            label="Numar CMR"
            disabled={editItem?.status === 'PROCESSED'}
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.cmrNumber}
            helperText={formik.errors.cmrNumber as string}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.truckNumber}
            onChange={event =>
              formik.setFieldValue('truckNumber', (event.target as HTMLInputElement).value)
            }
            label="Număr camion"
            disabled={editItem?.status === 'PROCESSED'}
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.truckNumber}
            helperText={formik.errors.truckNumber as string}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.driverName}
            onChange={event =>
              formik.setFieldValue('driverName', (event.target as HTMLInputElement).value)
            }
            label="Nume sofer"
            disabled={editItem?.status === 'PROCESSED'}
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.driverName}
            helperText={formik.errors.driverName as string}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.providerName}
            onChange={event =>
              formik.setFieldValue('providerName', (event.target as HTMLInputElement).value)
            }
            label="Nume companie colectoare"
            disabled={editItem?.status === 'PROCESSED'}
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.providerName}
            helperText={formik.errors.providerName as string}
            required
          />
        </Grid>

        <DeleteConfirmationModal
          isModalOpen={isDeleteModalOpen}
          closeModal={closeDeleteModal}
          actionUrl="/shop-reception/returo-notice/detail/delete/:id"
          itemId={`${selectedSealId}`}
          cancelLabel="Nu"
          deleteLabel="Da"
          content={
            <Typography variant="regular">Esti sigur ca vrei sa stergi acest sigiliu?</Typography>
          }
          succesMessage="Sigiliul a fost sters cu succes."
          additionalAction={() => {
            const newSealsList = formik.values.returoSealModelList.filter(
              item => +item.id !== +selectedSealId
            )
            formik.setFieldValue('returoSealModelList', newSealsList)
            setTableItems(newSealsList)
          }}
        />
        {editItem?.status === 'PROCESSED' && tableItems.length === 0 ? (
          <Grid xs={12} item>
            <Typography variant="regular">Nu există sigilii cu statusul În predare</Typography>
          </Grid>
        ) : (
          <Grid xs={12} item>
            <FormTable columns={columns} rows={tableItems} />
          </Grid>
        )}

        <Grid xs={12} item className={s.modalButtons}>
          {!isSealListLoading && (
            <>
              <Button variant="outlined" label="Anuleaza" onClick={() => closeModal()} />
              <Button
                variant="contained"
                label={'Salveaza'}
                onClick={() => editItem?.status !== 'PROCESSED' && formik.handleSubmit()}
                disabled={editItem?.status === 'PROCESSED'}
              />
            </>
          )}
        </Grid>
      </Grid>
    </Modal>
  )
}

export default EditReportForm
